import { useState, useEffect, FormEvent } from 'react';
import AuthService from '../services/auth.service';

export default function SsoLoginPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const authService = AuthService.getInstance();

  // Test Frontegg's direct SSO prelogin endpoint (no email needed)
  useEffect(() => {
    if (!authService.ssoConfigId) return;
    const testDirectPrelogin = async () => {
      // Try custom domain path (as Frontegg support suggested)
      try {
        const response = await fetch(
          `/team/resources/sso/v1/auth/prelogin/${authService.ssoConfigId}`,
          { method: 'POST' }
        );
        const data = await response.text();
        console.log('[SSO Direct Prelogin - custom domain] Status:', response.status);
        console.log('[SSO Direct Prelogin - custom domain] Response:', data);
      } catch (err) {
        console.error('[SSO Direct Prelogin - custom domain] Error:', err);
      }
      // Try auth domain without frontegg prefix
      try {
        const response = await fetch(
          `${authService.authUrl}/team/resources/sso/v1/auth/prelogin/${authService.ssoConfigId}`,
          { method: 'POST' }
        );
        const data = await response.text();
        console.log('[SSO Direct Prelogin - auth domain] Status:', response.status);
        console.log('[SSO Direct Prelogin - auth domain] Response:', data);
      } catch (err) {
        console.error('[SSO Direct Prelogin - auth domain] Error:', err);
      }
    };
    testDirectPrelogin();
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/v1/auth/sso-prelogin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        setError(data.error || 'No SSO configuration found for this email.');
        setLoading(false);
        return;
      }

      const data = await response.json();
      if (data.address) {
        // Before redirecting to IdP, bounce through the main app so it can
        // store the returnTo and then forward to the IdP
        const returnTo = encodeURIComponent(window.location.origin);
        const returnToken = encodeURIComponent(authService.returnToken || '');
        const idpUrl = encodeURIComponent(data.address);
        window.location.href = `${authService.authAppUrl}/sso-bounce?idpUrl=${idpUrl}&returnTo=${returnTo}&returnToken=${returnToken}`;
      } else {
        setError('No SSO configuration found for this email.');
        setLoading(false);
      }
    } catch {
      setError('Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <div className="w-full max-w-sm p-8">
        <h1 className="text-2xl font-bold text-gray-900 text-center mb-6">Sign In</h1>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email address
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              required
              autoFocus
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {error && (
            <p className="text-sm text-red-600">{error}</p>
          )}

          <button
            type="submit"
            disabled={loading || !email.trim()}
            className="w-full py-2 px-4 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Redirecting...' : 'Continue'}
          </button>
        </form>
      </div>
    </div>
  );
}
